/* The exercises as the learner meets them.
 *
 * Two things here are load-bearing and were learned the hard way in the
 * artifact: the carry item (one extra scenario from a prerequisite chapter,
 * graded with this chapter's own categories) is merged in once and memoised so
 * that render and grading always agree; and every presentation order is
 * re-rolled per attempt, rejecting any permutation whose answer key can be read
 * off the sequence.
 */
import { CARRY } from '../data/carry';
import type { Block, Exercise } from '../types';

const cache = new Map<string, Exercise>();

export function exOf(block: Block): Exercise | null {
  if (!block.ex) return null;
  const hit = cache.get(block.id);
  if (hit) return hit;

  const carry = CARRY[block.id];
  const ex = block.ex;
  let merged: Exercise = ex;

  if (carry && ex.type === 'classify' && carry.bin !== undefined && carry.whyKey) {
    merged = {
      type: 'classify',
      binsKey: ex.binsKey,
      binCount: ex.binCount,
      items: [...ex.items, { tKey: carry.tKey, bin: carry.bin, whyKey: carry.whyKey, carry: carry.from }],
    };
  } else if (carry && ex.type === 'choice' && carry.fbKey) {
    merged = {
      type: 'choice',
      options: [...ex.options, { tKey: carry.tKey, ok: false, fbKey: carry.fbKey, carry: carry.from }],
    };
  }

  cache.set(block.id, merged);
  return merged;
}

export function __clearExerciseCache(): void {
  cache.clear();
}

/* ── deterministic shuffling ──────────────────────────────────────── */
export function shuffle(n: number, seed: string): number[] {
  const a: number[] = [];
  for (let i = 0; i < n; i++) a.push(i);
  let s = 0;
  for (let c = 0; c < seed.length; c++) s = (s * 31 + seed.charCodeAt(c)) % 9973;
  for (let j = a.length - 1; j > 0; j--) {
    s = (s * 1103515245 + 12345) % 2147483647;
    const k = Math.abs(s) % (j + 1);
    const tmp = a[j];
    a[j] = a[k];
    a[k] = tmp;
  }
  return a;
}

export function isIdentity(perm: number[]): boolean {
  return perm.every((v, i) => v === i);
}

/* The key used to cycle with the item index — 0,1,0,1 across four chapters —
   so the answer sat in the positions. Reject any order whose key still cycles. */
export function isCyclic(seq: number[], k: number): boolean {
  if (seq.length < 3) return false;
  for (let i = 1; i < seq.length; i++) if (seq[i] !== (seq[i - 1] + 1) % k) return false;
  return true;
}

function keySequence(perm: number[], items: { bin: number }[]): number[] {
  return perm.map((p) => items[p].bin);
}

export function itemOrder(items: { bin: number }[], bins: number, seed: string): number[] {
  let perm = shuffle(items.length, seed);
  for (let s = 1; s < 60; s++) {
    if (!isCyclic(keySequence(perm, items), bins) && !isIdentity(perm)) return perm;
    perm = shuffle(items.length, seed + '|' + s);
  }
  return perm;
}

export function columnOrder(size: number, seed: string, banIdentity: boolean): number[] {
  let perm = shuffle(size, seed);
  for (let s = 1; s < 60; s++) {
    if (!(banIdentity && isIdentity(perm))) return perm;
    perm = shuffle(size, seed + '|c' + s);
  }
  return perm;
}

/* The correct option must not sit in the position it occupies in the data, nor
   first — both were readable without reading the chapter. */
export function optionOrder(options: { ok: boolean }[], seed: string): number[] {
  const correct = options.findIndex((o) => o.ok);
  let perm = shuffle(options.length, seed);
  for (let s = 1; s < 60; s++) {
    const at = perm.indexOf(correct);
    if (at !== 0 && at !== correct) return perm;
    perm = shuffle(options.length, seed + '|o' + s);
  }
  return perm;
}

/* ── decision trees ───────────────────────────────────────────────── */
export function treeComplete(block: Block, path: number[]): boolean {
  let level = block.tree;
  let depth = 0;
  while (level) {
    const chosen = path[depth];
    if (chosen === undefined) return false;
    const branch = level.branches[chosen];
    if (!branch) return false;
    if (!branch.then) return true;
    level = branch.then;
    depth++;
  }
  return false;
}
